/**
 * auditStatus — is the persisted site audit CURRENT and CLEAN?
 *
 * The audit result is persisted (Firestore `{site}/audit/latest`) and hydrated
 * into the store, so it outlives the session that produced it. That makes a
 * naive `blocks === 0 → clean` check dishonest: a run from weeks ago would keep
 * showing a green tick over reports it never examined, and over reports that
 * have since been regenerated.
 *
 * So currency is part of the verdict, not a separate concern:
 *
 *   never run                        → null      (neutral — nothing asserted)
 *   stale (reports changed since)    → false     ("re-run", not "clean")
 *   current, blocking findings > 0   → false
 *   current, no blocking findings    → true
 *
 * Warnings and info findings are advisory and deliberately do NOT gate — only
 * `block` severity does, matching how the audit's own severity model is used
 * elsewhere.
 */

/** The audit fields this resolver needs. */
export interface AuditVerdictInput {
  /** When the persisted audit last ran (ISO). Absent ⇒ never run. */
  lastRunAt?: string | undefined;
  /** Report ids the audit actually examined. */
  reportIds?: readonly string[] | undefined;
  /** Findings by severity from the persisted result. */
  severityCounts?: Readonly<Record<string, number>> | undefined;
}

/** The report facts needed to judge whether the audit still describes them. */
export interface AuditReportRef {
  id: string;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
}

function timestamp(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * True when the persisted audit no longer describes the current report set —
 * a report appeared, or one it audited has changed since it ran.
 *
 * Reports that disappeared do NOT make it stale: auditing a superset is still
 * a valid statement about what remains.
 */
export function isAuditStale(
  audit: AuditVerdictInput,
  reports: readonly AuditReportRef[],
): boolean {
  const ranAt = timestamp(audit.lastRunAt);
  if (ranAt == null) return false; // never run — "stale" is not the right word
  const audited = new Set(audit.reportIds ?? []);
  for (const report of reports) {
    // A report the audit never saw.
    if (!audited.has(report.id)) return true;
    // A report that changed after the audit examined it.
    const touched = timestamp(report.updatedAt) ?? timestamp(report.createdAt);
    if (touched != null && touched > ranAt) return true;
  }
  return false;
}

/**
 * The `auditClean` input for {@link tabStatus} — null when never run, false
 * when stale or blocking, true only for a current audit with no blockers.
 */
export function resolveAuditClean(
  audit: AuditVerdictInput | null | undefined,
  reports: readonly AuditReportRef[],
): boolean | null {
  if (!audit?.lastRunAt) return null;
  if (isAuditStale(audit, reports)) return false;
  return (audit.severityCounts?.block ?? 0) === 0;
}
